import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DataFieldGroup } from 'src/app/shared/datafields/models';
import { JsonHttpService } from 'src/app/shared/http/json-http.service';
import { MonitorGroup } from '../models/monitor-group';
import { MonitorItem, MonitorItemContainer } from '../models/monitor-item';
import { MonitorSchedule } from '../models/monitor-schedule';
import { MonitorLayout, PresentationMonitorItemStatus } from '../models/monitor-status';

@Injectable({
    providedIn: 'root'
})
export class MonitorService {

    constructor(
        private http: JsonHttpService) {
    }

    // ***** Monitor Groups ***

    getMonitorGroups(): Promise<MonitorGroup[]> {
        return this.http.getTyped<MonitorGroup[]>('/api/monitor/groups/get-groups').toPromise();
    }

    saveMonitorGroup(group: MonitorGroup): Promise<MonitorGroup> {
        return this.http.post('/api/monitor/groups/save-group', group).toPromise();
    }

    deleteMonitorGroup(id: number): Promise<number> {
        const options = { params: new HttpParams().set('id', id.toString()) };
        return this.http.delete('/api/monitor/groups/delete-group', options).toPromise();
    }

    // ***** Monitor Items ***

    getMonitorItem(id: number): Promise<MonitorItem> {
        const options = { params: new HttpParams().set('id', id.toString()) };

        return this.http.getTyped<MonitorItem[]>('/api/monitor/items/get-items', options).pipe(
            map(items => items.shift())
        ).toPromise();
    }

    getMonitorItems(): Promise<MonitorItem[]> {
        return this.http.getTyped<MonitorItem[]>('/api/monitor/items/get-items').toPromise();
    }

    getHasEmbeddedMonitorType(artifactId: string): Observable<boolean> {
        const options = { params: new HttpParams().set('artifactId', artifactId) };
        return this.http.getTyped<boolean>('/api/monitor/items/get-has-embedded-monitortype', options, false).pipe(
            catchError(err => {
                console.error('ERROR in getHasEmbeddedMonitorType', err);
                return of(false);
            }));
    }

    getMonitorItemDataFieldGroups(artifactId: string, artifactConfig: string): Promise<DataFieldGroup[]> {
        const data = artifactConfig || null;
        const options = { params: new HttpParams().set('artifactId', artifactId) };
        return this.http.post('/api/monitor/items/get-itemdatafields', data, options).toPromise();
    }

    saveMonitorItem(item: MonitorItemContainer): Promise<MonitorItem> {
        return this.http.post('/api/monitor/items/save-item', item).toPromise();
    }

    saveMonitorItems(items: MonitorItemContainer[]): Promise<MonitorItem[]> {
        return this.http.post('/api/monitor/items/save-items', items).toPromise();
    }

    deleteMonitorItem(id: number): Promise<number> {
        const options = { params: new HttpParams().set('id', id.toString()) };
        return this.http.delete('/api/monitor/items/delete-item', options).toPromise();
    }

    // ***** Monitor Schedules ***

    getMonitorSchedules(): Promise<MonitorSchedule[]> {
        return this.http.get('/api/monitor/schedules/get-schedules').toPromise();
    }

    saveMonitorSchedule(group: MonitorSchedule): Promise<MonitorSchedule> {
        return this.http.post('/api/monitor/schedules/save-schedule', group).toPromise();
    }

    deleteMonitorSchedule(id: number): Promise<number> {
        const options = { params: new HttpParams().set('id', id.toString()) };
        return this.http.delete('/api/monitor/schedules/delete-schedule', options).toPromise();
    }

    // ***** Monitor Status ***

    getMonitorLayout(): Promise<MonitorLayout> {
        return this.http.get('/api/monitor/status/get-layout').toPromise();
    }

    getPresentationMonitorItemStatuses(monitorItemId: number): Promise<PresentationMonitorItemStatus[]> {
        const options = { params: new HttpParams().set('monitorItemId', monitorItemId.toString()) };
        return this.http.get('/api/monitor/status/get-monitor-item-statuses', options).toPromise();
    }
}
